import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';

export type FeedbackType = 'BUG' | 'IDEA' | 'QUESTION';
export type FeedbackStatus = 'NEW' | 'SEEN' | 'DONE';

/** Body of POST /feedback. Mirrors FeedbackRequestDTO; the server keeps institutionId only if the caller is a member. */
export interface FeedbackRequest {
    type: FeedbackType;
    message: string;
    institutionId?: string | null;
    pagePath?: string;
    roleNames?: string;
    appVersion?: string;
    userAgent?: string;
}

/** A screenshot or PDF sent with a message. Mirrors FeedbackAttachmentDTO. */
export interface FeedbackAttachment {
    id: string;
    fileName: string;
    contentType: string;
    sizeBytes: number;
}

/** Same limits as UserFeedbackService: up to 3 files per message, 5 MB each, PNG / JPEG / PDF. */
export const FEEDBACK_ATTACHMENT_MAX = 3;
export const FEEDBACK_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
export const FEEDBACK_ATTACHMENT_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];

/** One of the caller's own messages. Mirrors FeedbackDTO. */
export interface Feedback {
    id: string;
    type: FeedbackType;
    message: string;
    pagePath: string | null;
    status: FeedbackStatus;
    reply: string | null;
    repliedAt: string | null;
    createdAt: string;
    attachments: FeedbackAttachment[];
}

/** Inbox row for Kervolt staff. Mirrors PlatformFeedbackDTO. */
export interface PlatformFeedback extends Feedback {
    institutionId: string | null;
    institutionName: string | null;
    userId: string;
    username: string | null;
    roleNames: string | null;
    appVersion: string | null;
    userAgent: string | null;
    repliedByUsername: string | null;
}

export interface FeedbackPage<T> {
    content: T[];
    totalElements: number;
    number: number;
    size: number;
}

export interface PlatformFeedbackFilter {
    status?: FeedbackStatus | '';
    type?: FeedbackType | '';
    institutionId?: string;
    from?: string;
    to?: string;
    page?: number;
    size?: number;
}

/** Drops empty filter values so the backend only sees what was chosen. */
const clean = (params: object) =>
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));

export const FeedbackService = {
    send: async (body: FeedbackRequest): Promise<Feedback> => {
        const response = await axios.post<Feedback>(ENDPOINTS.FEEDBACK.SEND, body, {
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    mine: async (page = 0, size = 20): Promise<FeedbackPage<Feedback>> => {
        const response = await axios.get<FeedbackPage<Feedback>>(ENDPOINTS.FEEDBACK.MINE, {
            params: { page, size },
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    /** SystemAdmin / SaaSOperator only. */
    inbox: async (filter: PlatformFeedbackFilter): Promise<FeedbackPage<PlatformFeedback>> => {
        const response = await axios.get<FeedbackPage<PlatformFeedback>>(ENDPOINTS.FEEDBACK.PLATFORM, {
            params: clean(filter),
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    newCount: async (): Promise<number> => {
        const response = await axios.get<number>(ENDPOINTS.FEEDBACK.PLATFORM_NEW_COUNT, {
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    update: async (id: string, body: { status?: FeedbackStatus; reply?: string }): Promise<PlatformFeedback> => {
        const response = await axios.patch<PlatformFeedback>(ENDPOINTS.FEEDBACK.PLATFORM_UPDATE(id), body, {
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    /** Adds files to the caller's own message (multipart field "files"). */
    uploadAttachments: async (id: string, files: File[]): Promise<Feedback> => {
        const form = new FormData();
        files.forEach(f => form.append('files', f));
        const response = await axios.post<Feedback>(ENDPOINTS.FEEDBACK.ATTACHMENTS(id), form, {
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },

    /** Owner download; platform staff use the inbox URL (platform = true). */
    downloadAttachment: async (feedbackId: string, attachmentId: string, platform = false): Promise<Blob> => {
        const url = platform
            ? ENDPOINTS.FEEDBACK.PLATFORM_ATTACHMENT(feedbackId, attachmentId)
            : ENDPOINTS.FEEDBACK.ATTACHMENT(feedbackId, attachmentId);
        const response = await axios.get<Blob>(url, { responseType: 'blob', headers: AuthService.getAuthHeader() });
        return response.data;
    },
};
